import { GlLink, GlSprintf, GlAnimatedUploadIcon } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { __ } from '~/locale';
import { VALID_DATA_TRANSFER_TYPE, VALID_IMAGE_FILE_MIMETYPE } from './constants';
import { isValidImage } from './utils';
export default {
    name: 'UploadDropzone',
    components: {
        GlLink,
        GlSprintf,
        GlAnimatedUploadIcon
    },
    props: {
        displayAsCard: {
            type: Boolean,
            required: false,
            default: false
        },
        enableDragBehavior: {
            type: Boolean,
            required: false,
            default: false
        },
        uploadSingleMessage: {
            type: String,
            required: false,
            default: __('Drop or %{linkStart}upload%{linkEnd} file to attach')
        },
        uploadMultipleMessage: {
            type: String,
            required: false,
            default: __('Drop or %{linkStart}upload%{linkEnd} files to attach')
        },
        dropToStartMessage: {
            type: String,
            required: false,
            default: __('Drop your files to start your upload.')
        },
        isFileValid: {
            type: Function,
            required: false,
            default: isValidImage
        },
        validFileMimetypes: {
            type: Array,
            required: false,
            default: ()=>[
                    VALID_IMAGE_FILE_MIMETYPE.mimetype
                ]
        },
        singleFileSelection: {
            type: Boolean,
            required: false,
            default: false
        },
        inputFieldName: {
            type: String,
            required: false,
            default: 'upload_file'
        },
        inputFieldId: {
            type: String,
            required: false,
            default: undefined
        },
        shouldUpdateInputOnFileDrop: {
            type: Boolean,
            required: false,
            default: false
        },
        showUploadDesignOverlay: {
            type: Boolean,
            required: false,
            default: false
        },
        uploadDesignOverlayText: {
            type: String,
            required: false,
            default: ''
        },
        validateDesignUploadOnDragover: {
            type: Boolean,
            required: false,
            default: false
        },
        acceptDesignFormats: {
            type: String,
            required: false,
            default: ''
        },
        hideUploadTextOnDragging: {
            type: Boolean,
            required: false,
            default: false
        },
        hasUploadError: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'change',
        'dragenter',
        'dragleave',
        'error'
    ],
    data () {
        return {
            dragCounter: 0,
            isDragDataValid: true,
            animateUploadIcon: false
        };
    },
    computed: {
        dragging () {
            return this.dragCounter !== 0;
        },
        iconStyles () {
            return {
                class: this.displayAsCard ? 'gl-mb-3' : 'gl-mr-3'
            };
        },
        showDropzoneOverlay () {
            if (this.validateDesignUploadOnDragover && this.acceptDesignFormats) {
                return this.dragging && this.isDragDataValid && !this.enableDragBehavior;
            }
            return this.dragging && !this.enableDragBehavior;
        }
    },
    methods: {
        isValidUpload (files) {
            return files.every(this.isFileValid);
        },
        isValidDragDataType ({ dataTransfer }) {
            return Boolean(dataTransfer && dataTransfer.types.some((t)=>t === VALID_DATA_TRANSFER_TYPE));
        },
        ondrop ({ dataTransfer = {} }) {
            this.dragCounter = 0;
            // User already had feedback when dropzone was active, so bail here
            if (!this.isDragDataValid) {
                return;
            }
            const { files } = dataTransfer;
            if (!this.isValidUpload(Array.from(files))) {
                this.$emit('error');
                return;
            }
            // NOTE: This is a temporary solution to integrate dropzone into a Rails
            // form. On file drop if `shouldUpdateInputOnFileDrop` is true, the file
            // input value is updated. So that when the form is submitted — the file
            // value would be send together with the form data. This solution should
            // be removed when License file upload page is fully migrated:
            // https://gitlab.com/gitlab-org/gitlab/-/issues/352501
            // NOTE: as per https://caniuse.com/mdn-api_htmlinputelement_files, IE11
            // is not able to set input.files property, thought the user would still
            // be able to use the file picker dialogue option, by clicking the
            // "openFileUpload" button
            if (this.shouldUpdateInputOnFileDrop) {
                // Since FileList cannot be easily manipulated, to match requirement of
                // singleFileSelection, we're throwing an error if multiple files were
                // dropped on the dropzone
                // NOTE: we can drop this logic together with
                // `shouldUpdateInputOnFileDrop` flag
                if (this.singleFileSelection && files.length > 1) {
                    this.$emit('error');
                    return;
                }
                this.$refs.fileUpload.files = files;
            }
            this.$emit('change', this.singleFileSelection ? files[0] : files);
        },
        ondragenter (e) {
            this.$emit('dragenter', e);
            this.dragCounter += 1;
            this.isDragDataValid = this.isValidDragDataType(e);
        },
        ondragover ({ dataTransfer }) {
            if (this.validateDesignUploadOnDragover) {
                this.isDragDataValid = Array.from(dataTransfer.items).some((item)=>this.acceptDesignFormats.includes(item.type));
            }
        },
        ondragleave (e) {
            this.$emit('dragleave', e);
            this.dragCounter -= 1;
        },
        openFileUpload () {
            this.$refs.fileUpload.click();
        },
        // Public method: clears the native file input so a previously selected
        // file is not submitted with a surrounding form (e.g. after the consumer
        // discards the selection).
        clearInputFiles () {
            if (this.$refs.fileUpload) {
                this.$refs.fileUpload.value = '';
            }
        },
        onFileInputChange (e) {
            if (!this.isValidUpload(Array.from(e.target.files))) {
                // Clear the rejected file so it is not submitted with a surrounding
                // form on the next submit.
                this.clearInputFiles();
                this.$emit('error');
                return;
            }
            this.$emit('change', this.singleFileSelection ? e.target.files[0] : e.target.files);
        },
        onMouseEnter () {
            this.animateUploadIcon = true;
        },
        onMouseLeave () {
            this.animateUploadIcon = false;
        }
    }
};
